import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { useBusinessContext } from "../../context/ModelContext";
import { PageContent } from "../../components/PageContent/PageContent";
import { Box } from "@chakra-ui/layout";
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Checkbox,
  HStack,
  IconButton,
  SkeletonText,
  Tag,
  VStack,
} from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useToast } from "../../hooks/useToast";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { useSearchInput } from "../../hooks/useSearchInput";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import DateUtil from "../../util/DateUtil";

export const Redemptions = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const {
    checked: showDeletedConfiguration,
    onChange: onChangeDeletedVenuesCheck,
  } = useCheckboxState(false);
  const { showErrorToast, showSuccessToast } = useToast();
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search digital membership redemption forms...",
  });

  const {
    items: redemptionConfigs,
    refresh,
    isLoading,
    error,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getRedemptionFormConfigurations(business.BusinessId as string, {
        q: debouncedQuery,
        includeDeleted: showDeletedConfiguration ? "true" : "false",
      }),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getRedemptionFormConfigurations(business.BusinessId as string, {
        q: debouncedQuery,
        includeDeleted: showDeletedConfiguration ? "true" : "false",
        continuationToken,
      }),
    [business.BusinessId, debouncedQuery, showDeletedConfiguration]
  );

  const deleteRedemptionForm = useAdminApiUpdateRequest(
    (apiClient, redemptionId: string) =>
      apiClient.deleteRedemptionFormConfiguration(
        business.BusinessId!,
        redemptionId
      ),
    [redemptionConfigs]
  );

  const restoreRedemptionForm = useAdminApiUpdateRequest(
    (apiClient, redemptionId: string) =>
      apiClient.restoreRedemptionFormConfiguration(
        business.BusinessId!,
        redemptionId
      ),
    [redemptionConfigs]
  );

  useEffect(() => {
    if (deleteRedemptionForm.result) {
      if (deleteRedemptionForm.result.ok) {
        showSuccessToast("Digital membership redemption form deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteRedemptionForm.result.message ??
            "Failed to delete digital membership redemption form"
        );
      }
    }
  }, [deleteRedemptionForm.result]);

  useEffect(() => {
    if (restoreRedemptionForm.result) {
      if (restoreRedemptionForm.result.ok) {
        showSuccessToast("Digital membership redemption form restored.");
        refresh();
      } else {
        showErrorToast(
          restoreRedemptionForm.result.message ??
            "Failed to delete digital membership redemption form"
        );
      }
    }
  }, [restoreRedemptionForm.result]);

  const goToDetail = (e: React.MouseEvent<HTMLElement>) => {
    if (
      e.currentTarget.dataset.businessId &&
      e.currentTarget.dataset.redemptionId
    ) {
      navigate(
        `/admin/businesses/${e.currentTarget.dataset.businessId}/redemptions/${e.currentTarget.dataset.redemptionId}`
      );
    }
  };

  const deleteRedemptionFormConfiguration = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    if (e.currentTarget.dataset.redemptionId) {
      const redemptionId = e.currentTarget.dataset.redemptionId;
      showConfirmationDialog(
        "Delete Digital Membership Redemption Form?",
        `Are you sure you wish to delete ${
          e.currentTarget.dataset.redemptionName ?? redemptionId
        }?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteRedemptionForm.request(redemptionId);
        }
      });
    }
  };

  const restoreRedemptionFormConfiguration = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    if (e.currentTarget.dataset.redemptionId) {
      const redemptionId = e.currentTarget.dataset.redemptionId;
      showConfirmationDialog(
        "Restore Digital Membership Redemption Form?",
        `Are you sure you wish to restore ${
          e.currentTarget.dataset.redemptionName ?? redemptionId
        }?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreRedemptionForm.request(redemptionId);
        }
      });
    }
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          Digital Membership Redemption Forms
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%" alignItems="flex-start">
          <BusinessBreadcrumbs variant="pageHeading" mb={4}>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/redemptions`}
              >
                Digital Membership Redemption Forms
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeletedConfiguration}
                onChange={onChangeDeletedVenuesCheck}
                title="Include deleted digital membership redemption form in the list."
              >
                Include deleted
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={() =>
                  navigate(
                    `/admin/businesses/${business.BusinessId}/redemptions/new`
                  )
                }
              >
                New Digital Membership Redemption Form
              </BusinessMenu>
            </Box>
          </HStack>
          {redemptionConfigs && (
            <Box width="100%">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th w="0.1%">#</Th>
                    <Th w="85%">Name</Th>
                    <Th>Status</Th>
                    <Th>Created</Th>
                    <Th>Last Modified</Th>
                    <Th>&nbsp;</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {isLoading && (
                    <Tr>
                      <Td width="5%">
                        <SkeletonText noOfLines={12} spacing="4" width="100%" />
                      </Td>
                      <Td width="65%">
                        <SkeletonText noOfLines={12} spacing="4" width="100%" />
                      </Td>
                      <Td width="5%" whiteSpace="nowrap">
                        <SkeletonText noOfLines={12} spacing="4" width="100%" />
                      </Td>
                      <Td width="5%" whiteSpace="nowrap">
                        <SkeletonText noOfLines={12} spacing="4" width="100%" />
                      </Td>
                      <Td width="10%" whiteSpace="nowrap">
                        <SkeletonText noOfLines={12} spacing="4" width="100%" />
                      </Td>
                      <Td width="10%" whiteSpace="nowrap">
                        <SkeletonText noOfLines={12} spacing="4" width="100%" />
                      </Td>
                    </Tr>
                  )}
                  {!isLoading &&
                    redemptionConfigs.map((row) => {
                      return (
                        <Tr key={row.Meta?.Id}>
                          <Td w="0.1%">{row.RedemptionFormConfigurationNo}</Td>
                          {row.Meta?.IsDeleted ? (
                            <Td>
                              <Tag mr={2}>Deleted</Tag>
                              {row.DisplayName}
                            </Td>
                          ) : (
                            <Td
                              w="85%"
                              key={row.Meta?.Id}
                              data-redemption-id={row.Meta?.Id}
                              data-business-id={business.BusinessId}
                              onClick={goToDetail}
                              _hover={{
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              {row.DisplayName}
                            </Td>
                          )}
                          <Td>
                            {row.IsEnabled ? (
                              <Tag size="sm">Enabled</Tag>
                            ) : (
                              <Tag
                                size="sm"
                                bgColor="cherryButton.500"
                                color="white"
                              >
                                Disabled
                              </Tag>
                            )}
                          </Td>
                          <Td width="0.1%" whiteSpace="nowrap">
                            {DateUtil.getLocalDateFormatFromString(
                              row.Meta?.DateCreated
                            )}
                          </Td>
                          <Td width="0.1%" whiteSpace="nowrap">
                            {DateUtil.getLocalDateFormatFromString(
                              row.Meta?.DateUpdated
                            )}
                          </Td>
                          <Td>
                            {row.Meta?.IsDeleted && (
                              <IconButton
                                aria-label="Restore"
                                title="Restore"
                                icon={<RepeatClockIcon />}
                                size="xs"
                                isDisabled={false}
                                data-redemption-id={row.Meta?.Id}
                                data-redemption-name={row.DisplayName}
                                onClick={restoreRedemptionFormConfiguration}
                              />
                            )}
                            {!row.Meta?.IsDeleted && (
                              <IconButton
                                aria-label="Delete"
                                title="Delete"
                                icon={<DeleteIcon />}
                                size="xs"
                                isDisabled={false}
                                data-redemption-id={row.Meta?.Id}
                                data-redemption-name={row.DisplayName}
                                onClick={deleteRedemptionFormConfiguration}
                              />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                </Tbody>
              </Table>
            </Box>
          )}
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
